
import { notification } from '../../components/Notifications'
import { SurveyQuestionType } from '../../models/Survey'
import { type ISurveySection } from '../../store/slices/SurveyTemplateSlice'
import { type DescriptiveQuestion, SurveyItemQuestionType, type LinearQuestion, type MultiChoiceQuestion, type Question, type FreeResponseQuestion, type ILinearObject, BranchingOperation, type ISurveyItemBranchingFilters, type IBranchingSurveyItems } from './types'

export const updateElementBorderColor = (id: string, color: string): void => {
  const element = document.getElementById(id)
  if (element) {
    element.style.borderColor = color
  }
}

export const scrollToElement = (id: string, color: string = 'red'): void => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    })
    updateElementBorderColor(id, color)
  }
}

export const findInvalidSection = (sections: ISurveySection[]): string | undefined => {
  if (sections) {
    for (const section of sections) {
      const { surveyItems, surveySectionId } = section
      if (!Array.isArray(surveyItems) || surveyItems.length === 0) {
        return surveySectionId
      }
      updateElementBorderColor(surveySectionId ?? '', '#D5D9DC')
    }
  }
}

const multiChoiceQuestionValidation = (question: MultiChoiceQuestion): boolean => {
  const options = question?.object || []
  const validOptions = options.every((option: string) => option !== '')
  const uniqueOptions = new Set(options).size === options.length

  if (!validOptions || question?.prompt === '' || !uniqueOptions) {
    return false
  }

  return true
}

const linearQuestionValidation = (question: LinearQuestion): boolean => {
  const options = question?.object || []

  const validOptions = options.every((option: ILinearObject) => option?.option !== '')

  const optionValues = options?.map((option: ILinearObject) => option.option)
  const uniqueOptions = new Set(optionValues)
  const allUnique = uniqueOptions.size === optionValues.length

  if (!validOptions || question?.prompt === '' || !allUnique) {
    return false
  }

  return true
}

const descriptiveQuestionValidation = (question: DescriptiveQuestion): boolean => {
  if (question.prompt === '') {
    return false
  }
  return true
}

const freeResponseQuestionValidation = (question: FreeResponseQuestion): boolean => {
  if (question?.prompt === '') {
    return false
  }
  return true
}

export const checkQuestionValidation = (question: Question): boolean => {
  switch (question?.itemType) {
    case SurveyItemQuestionType.MULTIPLE_CHOICE:
    case SurveyItemQuestionType.MULTI_SELECT:
      return multiChoiceQuestionValidation(question as MultiChoiceQuestion)
    case SurveyItemQuestionType.LINEAR_SCALE:
      return linearQuestionValidation(question as LinearQuestion)
    case SurveyItemQuestionType.FREE_RESPONSE:
      return freeResponseQuestionValidation(question as FreeResponseQuestion)
    case SurveyItemQuestionType.DESCRIPTIVE_TEXT:
      return descriptiveQuestionValidation(question as DescriptiveQuestion)
    default:
      return true
  }
}

export const findInvalidQuestion = (sections: ISurveySection[]): string | null | undefined => {
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex]
    if (!section.surveyItems) continue

    const questionIndex = section.surveyItems.find(question => !checkQuestionValidation(question))
    if (questionIndex) {
      return questionIndex.itemId
    }
  }
  return null
}

//  create branch filters based on the operation
export const preparedBranchFilterForUpdateFilters = (operation: BranchingOperation, answer: any[], section: ISurveySection): IBranchingSurveyItems[] => {
  const branchFilter: IBranchingSurveyItems[] = []

  // Based on the operation, create the appropriate branch filter
  if (operation === BranchingOperation.Is) {
    answer.forEach((a: any) => {
      branchFilter.push({
        key: a,
        surveySectionName: section.name,
        surveySectionId: section.surveySectionId
      })
    })
  } else if (operation === BranchingOperation.IsNotEmpty) {
    branchFilter.push({
      key: BranchingOperation.IsNotEmpty,
      surveySectionName: section.name,
      surveySectionId: section.surveySectionId
    })
  } else if (operation === BranchingOperation.IsEmpty) {
    branchFilter.push({
      key: BranchingOperation.IsEmpty,
      surveySectionName: section.name,
      surveySectionId: section.surveySectionId
    })
  }

  return branchFilter
}

// Check if Question option have Branch Section (checking only by single question option)
export const checkQuestionOptionHaveBranching = (index: number, question: Question | Record<string, any>): boolean => {
  const branchingKeys = question.branchingFilters
  const optionToDelete = question.itemType === SurveyQuestionType.LinearScale ? question?.object?.[index]?.option : question?.object?.[index]

  // Check if the option to delete has a branching filter associated
  const isOptionBranching = branchingKeys?.find((option: IBranchingSurveyItems) => option.key === optionToDelete)

  if (isOptionBranching) {
    const sectionName = question.branchingFilters?.[optionToDelete]?.name
    notification(
      'This option cannot be deleted because it contains a branch section',
      {
        variant: 'error',
        subTitle: `Linked Section: ${isOptionBranching.name || 'Unknown Section'}`
      }
    )
    return true
  }

  return false
}

// Get Child Branches section ids  By parent question
export const getUniqueChildBranchesDetails = (itemBranchFilters: ISurveyItemBranchingFilters): IBranchingSurveyItems[] | undefined => {
  const uniqueBranchingFilters = itemBranchFilters?.filter((value, index, self) =>
    index === self?.findIndex((t) => (
      t.surveySectionId === value.surveySectionId
    ))
  )
  return uniqueBranchingFilters
}

// Helper function to update branching filters in a parent section
export const updateBranchingFiltersSectionNameInParentSection = (parentSurveySection: { surveyItems: any[] }, surveySectionId: any, newName: any): any => {
  parentSurveySection.surveyItems.forEach((surveyItem) => {
    if (surveyItem.branchingFilters) {
      surveyItem.branchingFilters.forEach((branchingFilter: IBranchingSurveyItems) => {
        if (branchingFilter.surveySectionId === surveySectionId) {
          branchingFilter.surveySectionName = newName
        }
      })
    }
  })

  return parentSurveySection
}

/**
 * Finds the parent section and corresponding parent question for a given survey section,
 * based on its parentSurveySectionId and branchingFilters' itemId.
**/
export const findParentSectionAndQuestion = (sectionsList: ISurveySection[], section: ISurveySection): { parentSection: ISurveySection, parentQuestion: Question } | undefined => {
  const parentSection = sectionsList?.find((s: ISurveySection) => s.surveySectionId === section.parentSurveySectionId)
  const parentQuestion = parentSection?.surveyItems?.find((q: Question) => q.itemId === section?.branchingFilters?.itemId)

  if (parentSection && parentQuestion) {
    return { parentSection, parentQuestion }
  }

  return undefined
}

// Determines if an operation option should be disabled based on existing branching filters or if the operation is 'IsEmpty' and the question is required.
export const isOperationOptionDisabled = (operation: { title: string, value: BranchingOperation }, questionData: Question | undefined): boolean | undefined => {
  const alreadyUsed = questionData?.branchingFilters?.find(b => b.key === operation.value) && true
  return alreadyUsed ?? (operation.value === BranchingOperation.IsEmpty && questionData?.isRequired)
}

export const checkQuestionOptionHaveBranch = (option: any, question?: Question): boolean => {
  // option.option is useing for linear question  {option:somevalue,weight}
  const isExist = !!question?.branchingFilters?.find(b => option.option ? option.option === b.key : option === b.key)
  return isExist
}

// Only Disabled that option which have linked to other branch section user can remove the currently linked option and more which have not linked to any branch section
export const checkQuestionOptionHaveBranchForFilterModal = (option: any, question: Question | undefined, section: ISurveySection | undefined): boolean => {
  const branchingKeys = question?.branchingFilters?.map(b => b.key)
  const optionValue = option?.option ?? option
  const x = branchingKeys?.includes(optionValue) && !section?.branchingFilters?.answer.includes(optionValue)
  return x ?? false
}
